import React, { useState, useEffect } from 'react';
import { BadgeCheck, Check, Clock, CreditCard, Download, Loader2, Menu, Pencil, Share2, X } from 'lucide-react';
import { createPayment, apiFetch } from '../api';
import { openPaymentWidget } from '../yookassa';
import { format, isValid, parse, parseISO } from 'date-fns';
import { PLAN_GENERATION_TYPES } from '../constants/planGenerationTypes';
import { fetchPlanFileBlob, openPlanDownload, requestPlanDownloadPath } from '../utils/planDownload';

const formatPlanDateTime = (value) => {
  if (!value) return '';
  const toDate = () => {
    if (value instanceof Date) return value;
    const raw = typeof value === 'string' ? value.trim() : `${value}`;
    // Handles "2025-11-27 09:43:22.845894" and "2025-11-27 09:43:22"
    const parsedWithMicros = parse(raw, 'yyyy-MM-dd HH:mm:ss.SSSSSS', new Date());
    if (isValid(parsedWithMicros)) return parsedWithMicros;
    const parsedNoMicros = parse(raw, 'yyyy-MM-dd HH:mm:ss', new Date());
    if (isValid(parsedNoMicros)) return parsedNoMicros;
    const parsedIso = parseISO(raw.replace(' ', 'T'));
    if (isValid(parsedIso)) return parsedIso;
    const fallback = new Date(raw);
    return isValid(fallback) ? fallback : null;
  };

  const date = toDate();
  return date ? format(date, 'dd.MM.yyyy HH:mm:ss') : `${value}`;
};

export default function AccountPage({
  plans,
  navigate,
  onEdit,
  onRetry,
  onDelete,
  onRename,
  onLogout,
  idea,
  setIdea,
  city,
  setCity,
  showModal,
  setShowModal,
  direction,
  setDirection,
  generationType,
  setGenerationType,
  onStart,
  useOwnFunds,
  setUseOwnFunds,
  ownFunds,
  setOwnFunds,
  onPaymentSuccess,
  onShareSite,
}) {
  const [downloadPlan, setDownloadPlan] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [downloadPlanStatus, setDownloadPlanStatus] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [planPrice, setPlanPrice] = useState(1000);

  useEffect(() => {
    document.title = 'СоцПлан | Личный кабинет';
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/config');
        if (res.ok) {
          const data = await res.json();
          if (typeof data.planPrice === 'number') setPlanPrice(data.planPrice);
        }
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(min-width: 768px)');
    const handler = (event) => {
      if (event.matches) {
        setMobileMenuOpen(false);
      }
    };
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  const statusLabels = {
    PROCESSING: 'генерируется',
    DONE: 'черновик',
    DRAFT: 'черновик',
    ERROR: 'генерация не удалась',
    NEED_REGENERATE: 'нужен повтор генерации',
  };

  const handleDownload = (p) => {
    setDownloadPlan(p);
  };

  const closeDownload = () => setDownloadPlan(null);

  const downloadFile = async (type) => {
    if (!downloadPlan) return;
    if (downloadPlanStatus === 'PROCESSING') return;
    setDownloading(type);
    try {
      const path = await requestPlanDownloadPath(downloadPlan.uuid, type);
      if (path) {
        openPlanDownload(path);
        closeDownload();
        return;
      }
      const blob = await fetchPlanFileBlob(downloadPlan.uuid, type);
      if (!blob) return;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `plan_${downloadPlan.uuid}.${type}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      closeDownload();
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    setDownloadPlanStatus(downloadPlan?.status || null);
  }, [downloadPlan]);

  useEffect(() => {
    if (!downloadPlan) return;
    const updated = plans?.find((p) => p.uuid === downloadPlan.uuid);
    if (updated && updated.status !== downloadPlan.status) {
      setDownloadPlan(updated);
    }
  }, [plans, downloadPlan]);

  useEffect(() => {
    if (!downloadPlan || downloadPlanStatus !== 'PROCESSING') return undefined;
    let active = true;
    const fetchStatus = async () => {
      try {
        const res = await apiFetch(`/plan/by-uuid/${downloadPlan.uuid}`, {
          planUuid: downloadPlan.uuid,
          silent: true,
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!active) return;
        if (data?.status && data.status !== downloadPlanStatus) {
          setDownloadPlanStatus(data.status);
          setDownloadPlan((prev) =>
            prev ? { ...prev, status: data.status, paymentStatus: data.paymentStatus ?? prev.paymentStatus } : prev
          );
        }
      } catch {}
    };
    fetchStatus();
    if (typeof window === 'undefined') return undefined;
    const interval = window.setInterval(fetchStatus, 5000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [downloadPlan, downloadPlanStatus]);

  const handlePay = async (plan) => {
    if (typeof window !== 'undefined' && typeof window.ym === 'function') {
      window.ym(104041659, 'reachGoal', 'PAY_IN_ACCOUNT');
    }
    const payment = await createPayment(plan.uuid, planPrice);
    openPaymentWidget(payment.confirmationToken, () => onPaymentSuccess?.(plan.uuid));
  };

  const hasMenuAction = (item) => Boolean(item.onClick);
  const handleMenuItem = (item, closeAfter = false) => {
    if (item.onClick) {
      item.onClick();
    }
    if (closeAfter) {
      setMobileMenuOpen(false);
    }
  };

  const handleNavigateHome = () => {
    setMobileMenuOpen(false);
    navigate('home');
  };

  const sidebarItems = [
    { title: 'На главную', onClick: handleNavigateHome },
    { title: 'Ваши бизнес планы', current: true },
    { title: 'Настройки', onClick: () => navigate('settings') },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Topbar */}
      <div className="sticky sticky-safe-top z-30 bg-white/80 backdrop-blur">
        <header className="border-b bg-white/80 backdrop-blur">
          <div className="mx-auto flex h-12 max-w-6xl items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                aria-label="Открыть меню"
                aria-expanded={mobileMenuOpen}
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="hidden md:flex items-center gap-3 text-sm text-slate-600">
                <button
                  onClick={handleNavigateHome}
                  className="text-sm font-medium text-slate-700 hover:text-slate-900"
                >
                  Главная
                </button>
                <span className="text-sm text-slate-400">→</span>
                <span className="text-sm font-semibold text-slate-900">Личный кабинет</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {onShareSite && (
                <button
                  type="button"
                  onClick={onShareSite}
                  aria-label="Поделиться сайтом"
                  className="inline-flex items-center justify-center gap-0 md:gap-2 px-2 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
                >
                  <Share2 className="h-4 w-4" />
                  <span className="hidden md:inline">Поделиться</span>
                </button>
              )}
              <button onClick={onLogout} className="text-sm text-slate-600 hover:text-slate-900">Выйти</button>
            </div>
          </div>
        </header>
        {mobileMenuOpen && (
          <div className="md:hidden border-b border-slate-200 bg-white px-4 py-3">
            <div className="text-sm font-semibold text-slate-900">Навигация</div>
            <div className="mt-2 space-y-1">
              {sidebarItems.map((item, index) => (
                <div key={index}>
                  {item.current ? (
                    <div className="px-2 py-2 rounded-md bg-slate-100 text-sm font-medium text-slate-900">
                      {item.title}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleMenuItem(item, true)}
                      className="block w-full rounded-md px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
                    >
                      {item.title}
                    </button>
                  )}
                </div>
              ))}
            </div>
            {onShareSite && (
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  onShareSite();
                }}
                className="mt-3 inline-flex w-full items-center justify-center gap-0 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:text-slate-900"
                aria-label="Поделиться сайтом"
              >
                <Share2 className="h-4 w-4" />
                <span className="sr-only">Поделиться</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Layout */}
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 px-4 py-5 md:grid-cols-12 md:gap-6 md:py-6">
        {/* Sidebar menu */}
        <aside className="hidden md:col-span-3 md:block">
          <div className="space-y-1 rounded-xl border bg-white p-3">
            {sidebarItems.map((item, index) => (
              item.current ? (
                <div key={index} className="rounded-md bg-slate-100 px-2 py-2 text-sm font-medium text-slate-900">
                  {item.title}
                </div>
              ) : (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleMenuItem(item)}
                  className="w-full rounded-md px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                >
                  {item.title}
                </button>
              )
            ))}
          </div>
        </aside>

        {/* Content */}
        <main className="col-span-12 md:col-span-9 space-y-4">
          <div className="rounded-xl border bg-white p-5">
            <textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              placeholder="Напишите вашу идею"
              className="w-full rounded-lg border bg-slate-50 px-4 py-3 text-[15px] outline-none ring-0 focus:bg-white focus:border-slate-300"
              rows={2}
            />
            <button
              onClick={() => setShowModal(true)}
              className="mt-3 inline-flex h-11 items-center justify-center rounded-lg bg-blue-600 px-5 text-white text-[15px] font-semibold shadow-sm hover:bg-blue-700 active:translate-y-[1px]"
            >
              Создать бизнес план
            </button>
          </div>
          {plans.length === 0 && (
            <div className="rounded-xl border bg-white p-5 text-sm text-slate-600">
              Сохранённых планов нет
            </div>
          )}
          {plans.map((p) => (
            <div key={p.uuid} className="rounded-xl border bg-white p-5">
              <div className="flex items-center gap-2">
                {p.paymentStatus === 'PAID' ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                    <BadgeCheck className="h-3 w-3" /> Оплачен
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                    <Clock className="h-3 w-3" /> Не оплачен
                  </span>
                )}
              </div>
              <div className="mt-1 flex items-center gap-2 w-full">
                {editingId === p.uuid ? (
                  <>
                    <input
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      className="w-full text-xl font-semibold text-slate-900 border-b border-slate-300 focus:outline-none"
                    />
                    <button
                      onClick={async () => {
                        await onRename(p.uuid, editingTitle);
                        setEditingId(null);
                        setEditingTitle('');
                      }}
                      className="flex-shrink-0"
                    >
                      <Check className="h-5 w-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <h1 className="text-xl font-semibold text-slate-900">{p.title}</h1>
                    <button
                      onClick={() => {
                        setEditingId(p.uuid);
                        setEditingTitle(p.title);
                      }}
                      className="flex-shrink-0"
                    >
                      <Pencil className="h-4 w-4 text-slate-500" />
                    </button>
              </>
            )}
          </div>
          <div className="text-sm text-slate-500 mb-4">
            Создан: {formatPlanDateTime(p.createdAt)} · Статус: {statusLabels[p.status] || p.status}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => handleDownload(p)}
                  className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm hover:bg-slate-50"
                  title={p.paymentStatus === 'PAID' ? 'Скачать' : 'Доступно после оплаты'}
                >
                  <Download className="h-4 w-4" /> Скачать
                </button>
                {p.status === 'ERROR' ? (
                  <button
                    onClick={() => onRetry(p)}
                    className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm hover:bg-slate-50"
                  >
                    Попробовать заново
                  </button>
                ) : (
                  <button
                    onClick={() => onEdit(p)}
                    className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm hover:bg-slate-50"
                  >
                    <Pencil className="h-4 w-4" /> Редактировать
                  </button>
                )}
                <button
                  onClick={() => onDelete(p.uuid)}
                  className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm hover:bg-slate-50 text-red-600"
                >
                  Удалить
                </button>
                {p.paymentStatus !== 'PAID' && (
                  <button
                    onClick={() => handlePay(p)}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
                  >
                    <CreditCard className="h-4 w-4" /> {`Оплатить ${new Intl.NumberFormat('ru-RU').format(planPrice)} ₽`}
                  </button>
                )}
              </div>
            </div>
          ))}
        </main>
      </div>

      {downloadPlan && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl relative">
            <button
              onClick={closeDownload}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
            {downloadPlan.paymentStatus === 'PAID' ? (
              <>
                <div className="text-lg font-semibold">Скачать файл</div>
                {downloadPlanStatus === 'PROCESSING' && (
                  <p className="mt-2 text-sm text-slate-600">
                    Генерация еще в процессе. Для скачивания файлов дождитесь завершения
                  </p>
                )}
                <div className="mt-4 flex items-center gap-3">
                  <button
                    onClick={() => downloadFile('pdf')}
                    disabled={downloadPlanStatus === 'PROCESSING' || downloading === 'pdf'}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {downloadPlanStatus === 'PROCESSING' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> PDF
                      </>
                    ) : downloading === 'pdf' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'PDF'
                    )}
                  </button>
                  <button
                    onClick={() => downloadFile('docx')}
                    disabled={downloadPlanStatus === 'PROCESSING' || downloading === 'docx'}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {downloadPlanStatus === 'PROCESSING' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> DOCX
                      </>
                    ) : downloading === 'docx' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'DOCX'
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-lg font-semibold mb-4">Бизнес план не оплачен</div>
                <button
                  onClick={() => handlePay(downloadPlan)}
                  className="inline-flex h-10 items-center justify-center rounded-lg bg-blue-600 px-4 text-white text-sm font-medium hover:bg-blue-700"
                >
                  Оплатить и скачать
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showModal && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
            <div className="text-lg font-semibold">Сгенерировать бизнес‑план</div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-[13px] text-slate-700">
                  Бизнес‑направление
                </label>
                <input
                  value={direction}
                  onChange={(e) => setDirection(e.target.value)}
                  placeholder="Напр.: Кондитерская / Парикмахерская / Груминг"
                  className="w-full rounded-lg border px-3 py-2 text-[15px]"
                />
              </div>
              <div>
                <label className="mb-1 block text-[13px] text-slate-700">
                  Направление генерации
                </label>
                <select
                  value={generationType}
                  onChange={(e) => setGenerationType(e.target.value)}
                  className="w-full rounded-lg border bg-white px-3 py-2 text-[15px]"
                >
                  {PLAN_GENERATION_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[12px] text-slate-500">
                  Выбор сохранится в бизнес‑плане и потом не редактируется.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-[13px] text-slate-700">Город</label>
                <input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Ваш город"
                  className="w-full rounded-lg border px-3 py-2 text-[15px]"
                />
              </div>
              <div>
                <label className="mb-1 block text-[13px] text-slate-700">
                  Опишите вашу идею
                </label>
                <textarea
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2 text-[15px] min-h-[120px]"
                />
              </div>
              <div>
                <label className="inline-flex items-center gap-2 text-[13px] text-slate-700">
                  <input
                    type="checkbox"
                    checked={useOwnFunds}
                    onChange={(e) => setUseOwnFunds(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Учитывать собственные средства в бизнес плане
                </label>
                {useOwnFunds && (
                  <input
                    type="number"
                    value={ownFunds}
                    onChange={(e) => setOwnFunds(e.target.value)}
                    placeholder="Сумма в ₽"
                    className="mt-2 w-full rounded-lg border px-3 py-2 text-[15px]"
                  />
                )}
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={onStart}
                disabled={!idea}
                className="inline-flex h-11 items-center justify-center rounded-lg bg-blue-600 px-5 text-white text-[15px] font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                Сгенерировать
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="h-11 rounded-lg border px-4 text-[15px]"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
